/**
 * Shared visual language for the Settings data tables (Users, Roles,
 * Invitations). Keeping the grid + card treatment in one place means the
 * pages stay visually consistent and on-theme — change it here, both update.
 *
 * Everything resolves from the MUI theme (palette/divider/tokens) — no
 * hardcoded colours — so it follows the active company's palette.
 */
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/** Outlined "card" that frames a data table — rounded, soft border, clipped.
 *  Typed as a plain style object (not SxProps) so it can be both passed
 *  directly to `sx` and spread/merged with page-specific overrides. */
export const tableCardSx: SystemStyleObject<Theme> = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
};

/** Clean, on-theme DataGrid: tinted uppercase headers, soft row hover, token borders. */
export const cleanGridSx: SystemStyleObject<Theme> = {
  border: 'none',
  '--DataGrid-rowBorderColor': (t: Theme) => t.palette.divider,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (t: Theme) => alpha(t.palette.primary.main, 0.045),
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: 11.5,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'text.secondary',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell': {
    borderColor: 'divider',
    fontSize: 13.5,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: (t: Theme) => alpha(t.palette.primary.main, 0.035),
  },
  '& .MuiDataGrid-footerContainer': { borderColor: 'divider' },
  '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
};
